import { Button } from "@radix-ui/themes";
import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import styled from "@emotion/styled";
import {
  MemCommonEditor,
  MemCommonEditorEventHandler,
  MemCommonEditorEventKind,
  MemCommonEditorInitializer,
  MemCommonEditorInstance,
  MemCommonEditorTheme,
  editorContextModule,
} from "@mem-labs/common-editor";
import { useQuery, useMutation } from "@tanstack/react-query";

import { FC, useCallback, useRef } from "react";
import { atlasRoutes } from "@/app/router/routes";
import { useNavigate } from "react-router-dom";
import { useAtlasAssetToken } from "@/contexts/atlas-asset-token";
import { buildAssetProxyConfig } from "@/modules/asset-proxy";

export const NoteContentDocumentViewer: FC<{ noteId: Uuid }> = ({ noteId }) => {
  const navigate = useNavigate();
  const { ensureToken } = useAtlasAssetToken();

  const noteContentResult = useQuery({
    queryKey: ["note-content-document-by-note-id", noteId],
    queryFn: () =>
      api.get("/internal/v1/notes/{note_id}/note-content-document", {
        params: {
          path: {
            note_id: noteId,
          },
        },
      }),
  });

  const editorInstanceRef = useRef<MemCommonEditorInstance | null>(null);

  const editorInitializer: MemCommonEditorInitializer = useCallback(async () => {
    const defaultEditorContext = editorContextModule.generateDefaultEditorContext();

    // Ensure we have a valid asset token for image proxying
    const tokenResult = await ensureToken();
    const assetProxyConfig = tokenResult
      ? buildAssetProxyConfig({ token: tokenResult.token, expiresAt: tokenResult.expiresAt })
      : undefined;

    return {
      encodedContent: noteContentResult.data?.data?.encoded_content ?? "",
      toolbar: false,
      readOnly: true,
      autoFocus: false,
      noteId,
      enableWebSockets: false,
      maxUpdateThrottleMillis: 2000,
      theme: MemCommonEditorTheme.Light,
      metadata: undefined,
      webSocketAuthToken: undefined,
      context: {
        ...defaultEditorContext,
        assetProxy: assetProxyConfig,
      },
    };
  }, [noteId, noteContentResult.data?.data?.encoded_content, ensureToken]);

  const editorEventHandler: MemCommonEditorEventHandler = useCallback(event => {
    console.log(event);
    if (
      [MemCommonEditorEventKind.ContentError, MemCommonEditorEventKind.FatalError].includes(
        event.kind
      )
    ) {
      alert("Error: " + event.kind);
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateMutation = useMutation<any, Error, any>({
    mutationFn: async ({ encodedContent }) => {
      if (!noteContentResult.data?.data?.id) {
        throw new Error("Note content document ID is required");
      }

      const response = await api.post(
        "/internal/v1/note-content-documents/{note_content_document_id}/update-using-full-state",
        {
          body: { encoded_content: encodedContent },
          params: {
            path: {
              note_content_document_id: noteContentResult.data?.data?.id,
            },
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      alert("Content updated successfully!");
    },
    onError: error => {
      alert(`Failed to update content: ${error.message}`);
    },
  });

  const handleUpdateContent = () => {
    const newEncodedContent = prompt("Paste the new encoded content string:");

    if (newEncodedContent) {
      const confirmFirst = window.confirm("Are you sure you want to update the note content?");

      if (confirmFirst) {
        updateMutation.mutate({ encodedContent: newEncodedContent });
      }
    }
  };

  if (!noteContentResult.data) {
    return <pre>Loading...</pre>;
  }

  return (
    <>
      <br />
      <Button onClick={() => navigate(atlasRoutes.notesSnapshots({ params: { noteId } }).path)}>
        View Note Snapshots
      </Button>
      <br />
      <br />
      <NoteContentEditorWrapper>
        <MemCommonEditor
          editorInstanceRef={editorInstanceRef}
          editorInitializer={editorInitializer}
          editorEventHandler={editorEventHandler}
        />
      </NoteContentEditorWrapper>
      <br />
      <br />
      <Button onClick={handleUpdateContent}>Update Note Content</Button>
      <br />
      <br />
      <br />
      <br />
      <br />
      <pre>{JSON.stringify(noteContentResult.data, null, 2)}</pre>
    </>
  );
};

const NoteContentEditorWrapper = styled.div`
  * {
    font-family:
      -apple-system, "system-ui", "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
  }
  font-weight: normal;
  border: 1px solid var(--gray-8);
  padding: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
  max-width: calc(90vw - 200px);
`;
